import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, Plus, Edit, Trash2, Eye, Star, AlertCircle,
  ArrowLeft, Save, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const CertificateTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    template_name: '',
    template_type: 'general',
    standard_code: '',
    html_template: '',
    css_styles: '',
    certificate_text_template: '',
    signature1_name: '',
    signature1_designation: '',
    signature2_name: '',
    signature2_designation: '',
    is_default: false,
    is_active: true
  });
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/certificates/templates`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch templates');

      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSend.append(key, formData[key]);
        }
      });

      const url = editingTemplate
        ? `${process.env.REACT_APP_API_BASE_URL}/api/certificates/templates/${editingTemplate.id}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/certificates/templates`;

      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataToSend
      });

      if (!response.ok) throw new Error('Failed to save template');

      toast.success(`Template ${editingTemplate ? 'updated' : 'created'} successfully`);
      setShowForm(false);
      setEditingTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      template_name: template.template_name || '',
      template_type: template.template_type || 'general',
      standard_code: template.standard_code || '',
      html_template: template.html_template || '',
      css_styles: template.css_styles || '',
      certificate_text_template: template.certificate_text_template || '',
      signature1_name: template.signature1_name || '',
      signature1_designation: template.signature1_designation || '',
      signature2_name: template.signature2_name || '',
      signature2_designation: template.signature2_designation || '',
      is_default: template.is_default || false,
      is_active: template.is_active !== undefined ? template.is_active : true
    });
    setShowForm(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/certificates/templates/${templateId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete template');
      }

      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(error.message || 'Failed to delete template');
    }
  };

  const resetForm = () => {
    setFormData({
      template_name: '',
      template_type: 'general',
      standard_code: '',
      html_template: '',
      css_styles: '',
      certificate_text_template: '',
      signature1_name: '',
      signature1_designation: '',
      signature2_name: '',
      signature2_designation: '',
      is_default: false,
      is_active: true
    });
  };

  const loadDefaultTemplate = () => {
    setFormData({
      ...formData,
      html_template: `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <style>
        @page { size: A4 landscape; margin: 0; }
        body {
            margin: 0; padding: 0;
            font-family: 'Georgia', serif;
            width: 297mm; height: 210mm;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            position: relative;
        }
        .container {
            width: 100%; height: 100%;
            padding: 40px;
            box-sizing: border-box;
            position: relative;
            background: white;
            margin: 20px;
            border: 15px solid #667eea;
        }
        .title {
            text-align: center; margin-top: 60px;
            font-size: 48px; font-weight: bold;
            color: #1a1a1a; text-transform: uppercase;
        }
        .participant-name {
            text-align: center;
            font-size: 36px;
            color: #667eea;
            margin: 40px 0;
        }
        .certificate-text {
            text-align: center;
            margin: 40px auto;
            max-width: 700px;
            font-size: 18px;
            line-height: 1.8;
        }
        .signatures {
            position: absolute;
            bottom: 80px;
            width: calc(100% - 80px);
            display: flex;
            justify-content: space-around;
        }
        .signature-block { text-align: center; }
        .signature-line {
            border-top: 2px solid #333;
            width: 180px;
            margin: 50px auto 10px;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="title">Certificate of Completion</div>
        <div class="participant-name">{{ name }}</div>
        <div class="certificate-text">{{ certificate_text }}</div>
        <div class="signatures">
            <div class="signature-block">
                <div class="signature-line"></div>
                <div><strong>{{ signature1_name }}</strong></div>
                <div>{{ signature1_designation }}</div>
            </div>
            <div class="signature-block">
                <div class="signature-line"></div>
                <div><strong>{{ signature2_name }}</strong></div>
                <div>{{ signature2_designation }}</div>
            </div>
        </div>
        <div style="position: absolute; bottom: 30px; right: 50px; font-size: 12px;">
            Certificate ID: {{ certificate_id }} | {{ issue_date }}
        </div>
    </div>
</body>
</html>`,
      certificate_text_template: 'has successfully completed the training program on {{ course }} conducted on {{ training_date }} at {{ location }}. The training duration was {{ duration }}.'
    });
    toast.success('Default template loaded. You can now customize it.');
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => {
              setShowForm(false);
              setEditingTemplate(null);
              resetForm();
            }}
            variant="outline"
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Templates
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">
            {editingTemplate ? 'Edit Template' : 'Create New Template'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.template_name}
                    onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                    placeholder="e.g., ISO 9001 Lead Auditor Template"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Type *
                  </label>
                  <select
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.template_type}
                    onChange={(e) => setFormData({ ...formData, template_type: e.target.value })}
                  >
                    <option value="general">General</option>
                    <option value="awareness">Awareness Training</option>
                    <option value="internal_auditor">Internal Auditor</option>
                    <option value="lead_auditor">Lead Auditor</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Standard Code (Optional)
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.standard_code}
                    onChange={(e) => setFormData({ ...formData, standard_code: e.target.value })}
                    placeholder="e.g., iso-9001, iso-27001"
                  />
                </div>

                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600"
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    />
                    <span className="text-sm font-medium text-gray-700">Set as Default</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-blue-600"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Template Content */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Template Design</CardTitle>
                <Button
                  type="button"
                  onClick={loadDefaultTemplate}
                  variant="outline"
                  size="sm"
                >
                  Load Default Template
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  HTML Template *
                  <span className="text-xs text-gray-500 ml-2">
                    (Use Jinja2 syntax: {'{{ name }}'}, {'{{ course }}'}, etc.)
                  </span>
                </label>
                <textarea
                  required
                  rows={12}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  value={formData.html_template}
                  onChange={(e) => setFormData({ ...formData, html_template: e.target.value })}
                  placeholder="Enter HTML template..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Certificate Text Template *
                </label>
                <textarea
                  required
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.certificate_text_template}
                  onChange={(e) => setFormData({ ...formData, certificate_text_template: e.target.value })}
                  placeholder="e.g., has successfully completed {{ course }} on {{ training_date }}..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Available variables: {'{{ name }}'}, {'{{ course }}'}, {'{{ training_date }}'}, {'{{ duration }}'}, {'{{ location }}'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional CSS (Optional)
                </label>
                <textarea
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  value={formData.css_styles}
                  onChange={(e) => setFormData({ ...formData, css_styles: e.target.value })}
                  placeholder="Enter additional CSS..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Signatures */}
          <Card>
            <CardHeader>
              <CardTitle>Signature Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Signature 1</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.signature1_name}
                      onChange={(e) => setFormData({ ...formData, signature1_name: e.target.value })}
                      placeholder="e.g., Director Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Designation
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.signature1_designation}
                      onChange={(e) => setFormData({ ...formData, signature1_designation: e.target.value })}
                      placeholder="e.g., Director"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Signature 2</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.signature2_name}
                      onChange={(e) => setFormData({ ...formData, signature2_name: e.target.value })}
                      placeholder="e.g., CEO Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Designation
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.signature2_designation}
                      onChange={(e) => setFormData({ ...formData, signature2_designation: e.target.value })}
                      placeholder="e.g., Chief Executive Officer"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 gap-2"
            >
              <Save className="h-4 w-4" />
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingTemplate(null);
                resetForm();
              }}
              variant="outline"
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Certificate Templates</h1>
          <p className="text-gray-600 mt-1">Manage certificate templates and designs</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => navigate('/admin/certificates')}
            variant="outline"
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Certificates
          </Button>
          <Button
            onClick={() => setShowForm(true)}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Create Template
          </Button>
        </div>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading templates...</p>
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No templates found</p>
            <p className="text-gray-500 text-sm mt-2">Create your first certificate template to get started</p>
            <Button
              onClick={() => setShowForm(true)}
              className="mt-4 bg-blue-600 hover:bg-blue-700"
            >
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{template.template_name}</CardTitle>
                    <div className="flex gap-2 mt-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {template.template_type}
                      </span>
                      {template.is_default && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Star className="h-3 w-3 mr-1" />
                          Default
                        </span>
                      )}
                      {!template.is_active && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {template.standard_code && (
                    <div className="text-sm">
                      <span className="text-gray-600">Standard:</span>{' '}
                      <span className="font-medium text-gray-900">{template.standard_code.toUpperCase()}</span>
                    </div>
                  )}
                  {template.signature1_name && (
                    <div className="text-sm text-gray-600">
                      Signatories: {template.signature1_name}
                      {template.signature2_name && `, ${template.signature2_name}`}
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={() => handleEdit(template)}
                      size="sm"
                      variant="outline"
                      className="flex-1 gap-2"
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(template.id)}
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50 gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
